import { useMemo, useState } from "react"
import LeakChart from "../../routes/org/_components/modal/leak-chart"
import { langStore } from "@/stores/lang-store"
import BackIcon from "../icons/back-icon"
import { heatmapBundle } from "./leak-heatmap-const"
import { monthDetailBundle } from "./leak-month-detail-const"

const LeakMonthDetail = ({ date, onBack, dailyData, maxVal, series, xLabels }) => {
  const { lang } = langStore()
  const isKo = lang === "ko"
  const year = date ? date.getFullYear() : 0
  const month = date ? date.getMonth() + 1 : 0
  const [selectedDay, setSelectedDay] = useState(null)

  const { daysArray, emptyDays } = useMemo(() => {
    if (!date) return { daysArray: [], emptyDays: [] }

    const daysInMonth = new Date(year, month, 0).getDate()
    const startDay = new Date(year, month - 1, 1).getDay()
    return {
      daysArray: Array.from({ length: daysInMonth }, (_, i) => i + 1),
      emptyDays: Array.from({ length: startDay }, (_, i) => i)
    }
  }, [year, month, date])

  const { filteredLabels, filteredSeries } = useMemo(() => {
    if (!selectedDay || !xLabels || !series || !date)
      return { filteredLabels: [], filteredSeries: [] }

    const targetDateStr = `${year}-${String(month).padStart(2, "0")}-${String(selectedDay).padStart(2, "0")}`

    const indices = []
    xLabels.forEach((label, idx) => {
      if (label.startsWith(targetDateStr)) {
        indices.push(idx)
      }
    })

    if (indices.length === 0) return { filteredLabels: [], filteredSeries: [] }

    const start = indices[0]
    const end = indices[indices.length - 1]

    const fLabels = xLabels.slice(start, end + 1)
    const fSeries = series.map((s) => ({
      ...s,
      data: s.data.slice(start, end + 1)
    }))

    return { filteredLabels: fLabels, filteredSeries: fSeries }
  }, [selectedDay, xLabels, series, year, month, date])

  const TEXT = {
    weekdays: monthDetailBundle.weekdays[lang],
    selectDate: monthDetailBundle.selectDate[lang],
    selectDesc: monthDetailBundle.selectDesc[lang],
    chartTitleSuffix: monthDetailBundle.chartTitleSuffix[lang]
  }

  if (!date) return null

  const headerDateText = isKo
    ? `${year}년 ${month}월`
    : `${heatmapBundle.months["en"][month - 1]} ${year}`

  const getChartTitle = () => {
    if (isKo) return `${month}월 ${selectedDay}일 ${TEXT.chartTitleSuffix}`

    const monthName = heatmapBundle.months["en"][month - 1]
    return `${monthName} ${selectedDay}, ${TEXT.chartTitleSuffix}`
  }

  return (
    <div className="h-full flex flex-col">
      {/* 1. 헤더 */}
      <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100 sticky top-0 bg-white z-10 pt-2">
        <button
          onClick={onBack}
          className="p-2 -ml-2 rounded-full hover:bg-gray-100 transition-colors"
        >
          <BackIcon />
        </button>
        <h4 className="text-xl font-bold text-gray-800">{headerDateText}</h4>
      </div>

      <div className="flex-1 pb-10">
        {/* 2. 달력 영역 */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-6">
          <div className="grid grid-cols-7 mb-2 border-b border-gray-100 pb-2">
            {TEXT.weekdays.map((day, idx) => (
              <div
                key={day}
                className={`text-center text-xs font-semibold ${idx === 0 ? "text-red-400" : idx === 6 ? "text-blue-400" : "text-gray-400"}`}
              >
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1 md:gap-2">
            {emptyDays.map((i) => (
              <div key={`empty-${i}`} />
            ))}

            {daysArray.map((day) => {
              const dateKey = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`
              const value = dailyData?.[dateKey] || 0
              const hasData = value > 0
              const isSelected = selectedDay === day

              const ratio = maxVal > 0 && hasData ? value / maxVal : 0
              let bgColor = "#f3f4f6"
              if (hasData) bgColor = `rgba(37, 99, 235, ${0.2 + ratio * 0.8})`

              return (
                <div
                  key={day}
                  onClick={() => hasData && setSelectedDay(day)}
                  className={`
                    aspect-square rounded-lg flex flex-col items-center justify-center relative
                    transition-all duration-200 cursor-pointer
                    ${isSelected ? "ring-2 ring-red-500 scale-105 z-10" : "border-transparent"}
                    ${!hasData && "opacity-50 cursor-default"}
                  `}
                  style={{ backgroundColor: bgColor }}
                >
                  <span
                    className={`text-xs md:text-sm font-bold ${hasData ? "text-white" : "text-gray-400"}`}
                  >
                    {day}
                  </span>
                  {hasData && (
                    <span className="text-[9px] text-white/90 font-medium mt-0.5">
                      {value.toFixed(0)}%
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* 3. 하단 그래프 영역 */}
        {selectedDay ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 animate-fade-in-up">
            <h5 className="font-bold text-lg text-gray-800 mb-2 py-1">{getChartTitle()}</h5>

            <div className="h-64 w-full">
              <LeakChart xLabels={filteredLabels} series={filteredSeries} hideToolbar={true} />
            </div>
          </div>
        ) : (
          <div className="bg-blue-50 rounded-xl p-6 text-center text-blue-800">
            <p className="font-semibold">{TEXT.selectDate}</p>
            <p className="text-sm text-blue-600 mt-1">{TEXT.selectDesc}</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default LeakMonthDetail
